// API para el portal del cliente (autoservicio).
// Cada función chequea en el backend (RLS o RPC) que el usuario es el titular.

import { supabase } from './supabase'
import { mpErrorHumano, extraerCodigoMP } from './mpErrorHumano'

/** Trae todas las suscripciones donde el user logueado es titular o beneficiario. */
export async function getMisSuscripciones(userId) {
  const { data, error } = await supabase
    .from('clientes')
    .select(`
      id, alias, barrio, direccion, apto, dia_entrega, ventana_horaria, semana_mes,
      tipo_vivienda, lat, lng, estado, pausa_hasta, dia_cobro_mes, modelo_cobro,
      kit_actual, fecha_inicio, fecha_proximo_cobro, notas, titular_id, beneficiario_id,
      mp_card_last4, mp_card_brand, mp_preapproval_id, mp_preapproval_status,
      fuera_zona, entrega_responsable, cancelled_at, cancelacion_motivo, updated_at,
      barrio_obj:barrios!clientes_barrio_id_fkey(id, nombre, dias),
      titular:profiles!clientes_titular_fkey(id, nombre, phone, email),
      beneficiario:profiles!clientes_beneficiario_fkey(id, nombre, phone, email),
      kit:kits!clientes_kit_actual_fkey(codigo, nombre, tier, precio_susc, personas)
    `)
    .or(`titular_id.eq.${userId},beneficiario_id.eq.${userId}`)
    .order('created_at', { ascending: false })
  if (error) { console.error('getMisSuscripciones:', error); return [] }
  return data || []
}

/** Próxima entrega programada de una suscripción. */
export async function getProximaEntrega(suscripcionId) {
  const { data, error } = await supabase
    .from('entregas')
    .select('id, numero_orden, fecha_programada, ventana_horaria, estado, kit_codigo')
    .eq('cliente_id', suscripcionId)
    .in('estado', ['programada', 'en_ruta'])
    .order('fecha_programada', { ascending: true })
    .limit(1)
    .maybeSingle()
  if (error) return null
  return data
}

/** Próximo pago pendiente de una suscripción. */
export async function getProximoPago(suscripcionId) {
  const { data, error } = await supabase
    .from('pagos')
    .select('id, monto, vence, estado, metodo, mes')
    .eq('cliente_id', suscripcionId)
    .in('estado', ['pendiente', 'atrasado'])
    .order('vence', { ascending: true, nullsFirst: false })
    .limit(1)
    .maybeSingle()
  if (error) return null
  return data
}

/** Historial de pagos. */
export async function getHistorialPagos(suscripcionId, limit = 20) {
  const { data } = await supabase
    .from('pagos')
    .select('id, monto, vence, mes, estado, metodo, pagado_at, notas')
    .eq('cliente_id', suscripcionId)
    .order('vence', { ascending: false, nullsFirst: false })
    .limit(limit)
  return data || []
}

/** Historial de entregas. */
export async function getHistorialEntregas(suscripcionId, limit = 20) {
  const { data } = await supabase
    .from('entregas')
    .select('id, numero_orden, fecha_programada, ventana_horaria, estado, kit_codigo, ts_entregada')
    .eq('cliente_id', suscripcionId)
    .order('fecha_programada', { ascending: false })
    .limit(limit)
  return data || []
}

/** Resumen completo del dashboard de un cliente (1 RPC). Incluye:
 *  - Próxima entrega (real desde DB o calculada según día/semana del barrio)
 *  - Fecha de corte D-2 (con snap a viernes)
 *  - Próximo pago pendiente
 *  - Bonificación 3 meses (mes actual, monto próximo cobro, ahorro)
 *  - Vecinos activos en la cuadra
 */
export async function getDashboardResumen(suscripcionId) {
  const { data, error } = await supabase.rpc('cliente_dashboard_resumen', {
    p_cliente_id: suscripcionId,
  })
  if (error) { console.error('getDashboardResumen:', error); return null }
  const r = data?.[0]
  if (!r) return null
  return {
    proximaEntregaReal: r.proxima_entrega_real,
    proximaEntregaCalc: r.proxima_entrega_calc,
    proximaEntregaCorte: r.proxima_entrega_corte,
    proximoPago: r.proximo_pago_id ? {
      id: r.proximo_pago_id,
      monto: Number(r.proximo_pago_monto),
      vence: r.proximo_pago_vence,
      estado: r.proximo_pago_estado,
    } : null,
    bonif: {
      mesActual:       Number(r.bonif_mes_actual),
      mesesRestantes:  Number(r.bonif_meses_restantes),
      montoFull:       Number(r.bonif_monto_full),
      montoNormal:     Number(r.bonif_monto_normal),
      montoProximo:    Number(r.bonif_monto_proximo),
      ahorroTotal:     Number(r.bonif_ahorro_total),
      aplica:          r.bonif_aplica,
    },
    vecinos: {
      cuadra:        Number(r.vecinos_cuadra),
      paraDescuento: Number(r.vecinos_para_descuento),
    },
  }
}

/** Ahorro total acumulado del cliente vs supermercado (suscripción + pedidos one-shot). */
export async function getAhorroAcumulado(userId) {
  const { data, error } = await supabase.rpc('cliente_ahorro_acumulado', { p_user_id: userId })
  if (error) { console.error('getAhorroAcumulado:', error); return null }
  return data?.[0] || null
}

/** Fix UX 7-jul-2026: fecha REAL del próximo cobro D-1 de suscripción.
 *  Salta la entrega actual si ya está pagada/entregada y calcula el próximo
 *  jueves/día_entrega de la N-ésima semana del mes siguiente.
 *  Devuelve { fecha_entrega, fecha_cobro, monto } o null.
 */
export async function getProximoCobro(suscripcionId) {
  if (!suscripcionId) return null
  const { data, error } = await supabase.rpc('cliente_proximo_cobro', { p_cliente_id: suscripcionId })
  if (error) { console.warn('getProximoCobro:', error.message); return null }
  return data?.[0] || null
}

/* ──────────── ADMIN PEDIDOS ──────────── */

export async function adminGetPedidos({ estado = null, limit = 50, soloB2C = false, soloB2B = false } = {}) {
  const { data, error } = await supabase.rpc('admin_get_pedidos', {
    p_estado: estado, p_limit: limit,
    p_solo_b2c: soloB2C, p_solo_b2b: soloB2B,
  })
  if (error) { console.error('adminGetPedidos:', error); return [] }
  return data || []
}

export async function adminMarcarPedidoPagado(pedidoId, referencia = null) {
  const { data, error } = await supabase.rpc('admin_marcar_pedido_pagado', { p_pedido_id: pedidoId, p_referencia: referencia })
  if (error) throw error
  return data
}

export async function adminCancelarPedido(pedidoId, motivo = null) {
  const { data, error } = await supabase.rpc('admin_cancelar_pedido', { p_pedido_id: pedidoId, p_motivo: motivo })
  if (error) throw error
  return data
}

/* ──────────── ADMIN — Transiciones de estado de pedido one-shot ─────────── */
// Feature 19-jul-2026: autonomía operativa. Nico marca sus propios estados sin
// pedirle SQL a nadie. Migración 20260719120000_admin_pedidos_estados_y_stock.sql.

export async function adminMarcarPedidoEnArmado(pedidoId) {
  const { data, error } = await supabase.rpc('admin_marcar_pedido_en_armado', { p_pedido_id: pedidoId })
  if (error) throw error
  return data
}

export async function adminMarcarPedidoEnRuta(pedidoId) {
  const { data, error } = await supabase.rpc('admin_marcar_pedido_en_ruta', { p_pedido_id: pedidoId })
  if (error) throw error
  return data
}

/**
 * Marca pedido como entregado y descuenta stock (por default).
 * @param {string} pedidoId
 * @param {boolean} descontarStock - default true. Poner false solo si stock ya fue ajustado a mano.
 */
export async function adminMarcarPedidoEntregado(pedidoId, descontarStock = true) {
  const { data, error } = await supabase.rpc('admin_marcar_pedido_entregado', {
    p_pedido_id: pedidoId,
    p_descontar_stock: descontarStock,
  })
  if (error) throw error
  return data  // { ok, estado, entrega_id, stock_descontado, stock_detalle: [...] }
}

/**
 * Editar datos de envío del pedido — patch parcial con solo los campos que cambian.
 * @param {string} pedidoId
 * @param {object} data - Campos opcionales: fecha_entrega_programada (YYYY-MM-DD),
 *                       entrega_ventana ('manana'|'tarde'|'noche'), entrega_direccion,
 *                       entrega_apto, entrega_barrio_id (superadmin), notas
 * @param {object} opts - { ignorarBarrioDias: bool (superadmin), motivo: string }
 */
export async function adminEditarEnvioPedido(pedidoId, data, opts = {}) {
  const { data: res, error } = await supabase.rpc('admin_editar_envio_pedido', {
    p_pedido_id: pedidoId,
    p_data: data,
    p_ignorar_barrio_dias: !!opts.ignorarBarrioDias,
    p_motivo: opts.motivo || null,
  })
  if (error) throw error
  return res  // { ok, campos_cambiados: [...], diff: {...} }
}

/* ──────────── ADMIN — Libertad operativa one-shot (03-ago-2026) ──────────── */
// Nico: "quiero más libertad, no me deja hacer nada". RPCs:
//   admin_cambiar_estado_pedido / admin_reabrir_pedido_entregado /
//   admin_editar_pedido / admin_crear_pedido_manual
// Migraciones 20260803: admin_estado_libre_y_reabrir_pedido, admin_editar_pedido_completo,
//   admin_crear_pedido_manual.

/**
 * Cambiar el estado de un pedido a CUALQUIER estado no-terminal (adelante o atrás),
 * o a cancelado/rechazado. También reabre desde cancelado/rechazado (limpia esos campos).
 * NO sirve para pasar a 'entregado' (usá adminMarcarPedidoEntregado) ni para sacar de
 * 'entregado' (usá adminReabrirPedidoEntregado). Devuelve el estado FINAL real
 * (un automatismo B2C puede saltar pagado→en_armado).
 */
export async function adminCambiarEstadoPedido(pedidoId, nuevoEstado, motivo = null) {
  const { data, error } = await supabase.rpc('admin_cambiar_estado_pedido', {
    p_pedido_id: pedidoId, p_nuevo_estado: nuevoEstado, p_motivo: motivo,
  })
  if (error) throw error
  return data  // { ok, de, a, estado_final, nota }
}

/**
 * Reabrir un pedido ENTREGADO (solo superadmin). Repone el stock descontado
 * (movimiento devolucion → capa FIFO) y flag-ea CFE/COGS vigentes SIN tocarlos.
 * @param {string} pedidoId
 * @param {string} nuevoEstado - pendiente_pago | pagado | en_armado | en_ruta
 * @param {boolean} reponerStock - default true
 * @param {string|null} motivo
 */
export async function adminReabrirPedidoEntregado(pedidoId, nuevoEstado = 'pagado', reponerStock = true, motivo = null) {
  const { data, error } = await supabase.rpc('admin_reabrir_pedido_entregado', {
    p_pedido_id: pedidoId, p_nuevo_estado: nuevoEstado,
    p_reponer_stock: reponerStock, p_motivo: motivo,
  })
  if (error) throw error
  return data  // { ok, estado, repuso_stock, stock_repuesto: [...], warnings: [...] }
}

/**
 * Editar datos de un pedido más allá del envío: cliente, facturación (bloqueado si
 * ya hay CFE emitida), notas, ítems y montos (recalcula subtotal/total; bloqueado si
 * el pedido ya está entregado / con stock descontado). Patch parcial: solo las claves
 * presentes se tocan.
 * @param {string} pedidoId
 * @param {object} patch - claves opcionales: comprador_nombre, comprador_telefono,
 *   comprador_email, comprador_documento, facturacion_razon_social, facturacion_rut,
 *   facturacion_email, facturacion_direccion, notas, items (array),
 *   costo_envio, monto_subtotal
 * @param {string|null} motivo
 */
export async function adminEditarPedido(pedidoId, patch, motivo = null) {
  const { data, error } = await supabase.rpc('admin_editar_pedido', {
    p_pedido_id: pedidoId, p_patch: patch, p_motivo: motivo,
  })
  if (error) throw error
  return data  // { ok, monto_subtotal, costo_envio, monto_total }
}

/**
 * Crear un pedido one-shot a mano (pedidos por WhatsApp/teléfono).
 * @param {object} data - comprador_nombre (req), comprador_telefono, comprador_email,
 *   comprador_documento, tipo ('productos_sueltos'|'compra_unica'), items (array req salvo kit),
 *   kit_codigo, entrega_barrio_id (req salvo empresa_id), empresa_id, entrega_direccion (req),
 *   entrega_apto, entrega_ventana, fecha_entrega_programada (req YYYY-MM-DD), costo_envio,
 *   facturacion_razon_social, facturacion_rut, facturacion_email, facturacion_direccion,
 *   notas, metodo_pago, marcar_pagado (bool), pago_referencia
 */
export async function adminCrearPedidoManual(data) {
  const { data: res, error } = await supabase.rpc('admin_crear_pedido_manual', { p_data: data })
  if (error) throw error
  return res  // { ok, pedido_id, numero_orden, estado }
}

/* ──────────── ADMIN RECORDATORIOS WA ──────────── */

export async function adminGetRecordatorios(fecha = null) {
  const { data, error } = await supabase.rpc('admin_get_recordatorios', { p_fecha: fecha })
  if (error) { console.error('adminGetRecordatorios:', error); return [] }
  return data || []
}

export async function adminMarcarRecordatorioEnviado(fuente, id) {
  const { error } = await supabase.rpc('admin_marcar_recordatorio_enviado', { p_fuente: fuente, p_id: id })
  if (error) throw error
}

export async function adminMarcarTodosRecordatoriosEnviados(fecha = null) {
  const { data, error } = await supabase.rpc('admin_marcar_todos_recordatorios_enviados', { p_fecha: fecha })
  if (error) throw error
  return data
}

/* ──────────── ADMIN STOCK ──────────── */

export async function adminGetAlertasStock() {
  const { data, error } = await supabase.rpc('admin_alertas_stock')
  if (error) { console.error('adminGetAlertasStock:', error); return [] }
  return data || []
}

/* ──────────── ADMIN BARRIOS ──────────── */

export async function adminGetBarrios() {
  const { data, error } = await supabase.rpc('admin_get_barrios')
  if (error) { console.error('adminGetBarrios:', error); return [] }
  return data || []
}

export async function adminCrearBarrio({ nombre, dias, notas = null }) {
  const { data, error } = await supabase.rpc('admin_crear_barrio', {
    p_nombre: nombre, p_dias: dias, p_notas: notas,
  })
  if (error) throw error
  return data
}

export async function adminActualizarBarrio(id, { nombre, dias, notas = null, activo }) {
  const { data, error } = await supabase.rpc('admin_actualizar_barrio', {
    p_id: id, p_nombre: nombre, p_dias: dias, p_notas: notas, p_activo: activo,
  })
  if (error) throw error
  return data
}

/* ──────────── ACCIONES (RPCs) ──────────── */

export async function pausarSuscripcion(id, hasta = null) {
  const { error } = await supabase.rpc('cliente_pausar_suscripcion', {
    susc_id: id,
    hasta: hasta || null,
  })
  if (error) throw error
}

export async function reactivarSuscripcion(id) {
  const { error } = await supabase.rpc('cliente_reactivar_suscripcion', { susc_id: id })
  if (error) throw error
}

export async function cambiarDiaSuscripcion(id, dia, ventana) {
  const { error } = await supabase.rpc('cliente_cambiar_dia', {
    susc_id: id,
    dia_param: dia,
    ventana_param: ventana,
  })
  if (error) throw error
}

export async function cambiarKitSuscripcion(id, kitCodigo) {
  const { error } = await supabase.rpc('cliente_cambiar_kit', {
    susc_id: id,
    kit_codigo_param: kitCodigo,
  })
  if (error) throw error
}

/**
 * Cancela la suscripción del cliente. Devuelve resumen:
 * {
 *   ok: true,
 *   cliente_id, kit_actual,
 *   pagos_cancelados,           // cuántos pagos pendientes se anularon
 *   entregas_canceladas,        // entregas fuera del corte D-1 16h que se cancelaron
 *   entregas_en_corte,          // entregas ya armadas que se entregan igual
 *   mp_preapproval_id,
 *   requiere_cancelar_mp        // true si hay que llamar a mp-cancel-subscription
 * }
 */
export async function cancelarMiSuscripcion(id, motivo = null) {
  const { data, error } = await supabase.rpc('cliente_cancelar_suscripcion', {
    susc_id: id,
    motivo,
  })
  if (error) throw error
  return data
}

/**
 * Cancela el preapproval recurrente en Mercado Pago para una suscripción.
 * Solo hace falta llamarla si `cancelarMiSuscripcion` devolvió `requiere_cancelar_mp = true`.
 *
 * Devuelve:
 *   { ok: true, cancelled_local, cancelled_mp, mp_preapproval_id }
 *   o { ok: false, cancelled_local: true, cancelled_mp: false, error, mp_status }
 *
 * Si falla la llamada a MP igual marca el local como cancelled, así que la suscripción
 * del usuario queda cancelada igual (admin tiene que limpiar manualmente en MP después).
 */
export async function cancelarPreapprovalMP(clienteId) {
  const { data, error } = await supabase.functions.invoke('mp-cancel-subscription', {
    body: { cliente_id: clienteId },
  })
  if (error) throw error
  return data
}

/**
 * ¿Esta suscripción tiene un pedido ya armado (corte D-2 ya pasó)?
 * Si devuelve true, NO se puede cambiar la dirección desde el form —
 * hay que redirigir al cliente a coordinar por WhatsApp.
 */
export async function tienePedidoArmado(suscId) {
  const { data, error } = await supabase.rpc('tiene_pedido_armado', { susc_id: suscId })
  if (error) { console.error('tienePedidoArmado:', error); return false }
  return !!data
}

/**
 * Cambia la dirección de una suscripción. Valida barrio activo y bloquea
 * si hay pedido en corte (en ese caso el backend tira excepción con prefijo
 * "PEDIDO_EN_CORTE:" y la UI muestra el banner de WhatsApp).
 *
 * @param {string} suscId
 * @param {Object} params
 * @param {string} params.barrioId
 * @param {string} params.direccion
 * @param {string} [params.apto]
 * @param {string} [params.tipoVivienda]
 * @param {number} [params.lat]
 * @param {number} [params.lng]
 */
export async function cambiarDireccionSuscripcion(suscId, params) {
  const { data, error } = await supabase.rpc('cliente_cambiar_direccion_suscripcion', {
    susc_id:         suscId,
    barrio_id_p:     params.barrioId,
    direccion_p:     params.direccion,
    apto_p:          params.apto || null,
    tipo_vivienda_p: params.tipoVivienda || null,
    lat_p:           params.lat ?? null,
    lng_p:           params.lng ?? null,
  })
  if (error) throw error
  return data
}

/** Lista de kits disponibles (para el comparador básico). */
export async function getKitsDisponibles() {
  const { data } = await supabase
    .from('kits')
    .select('codigo, nombre, tier, precio_susc, personas, descripcion, imagen_url, destacado')
    .order('precio_susc', { ascending: true })
  return data || []
}

/** Trae kits con sus productos incluidos (para el comparador visual).
 *  Usa una RPC SECURITY DEFINER porque la tabla `productos` tiene RLS restrictivo
 *  (solo admin/repartidor) — la RPC expone únicamente los campos públicos. */
export async function getKitsConProductos() {
  const { data, error } = await supabase.rpc('cliente_get_kits_con_productos')
  if (error) { console.error('getKitsConProductos:', error); return [] }
  return (data || []).map(k => ({
    ...k,
    productos: Array.isArray(k.productos) ? k.productos : [],
  }))
}

/**
 * Productos destacados con stock efectivo (por grupo_equivalencia o SKU) > 0.
 * Usado en el carrusel de /cliente/Inicio.jsx (bloque "La Tienda Limpack").
 */
export async function getProductosDestacados(limit = 6) {
  const { data, error } = await supabase.rpc('cliente_get_productos_destacados', { p_limit: limit })
  if (error) { console.error('getProductosDestacados:', error); return [] }
  return data || []
}

/** Datos para mostrar como pagar (cuenta bancaria). */
export async function getCuentaCobroPublica() {
  const { data } = await supabase
    .from('app_config')
    .select('value')
    .eq('key', 'cuenta_cobro')
    .maybeSingle()
  try { return JSON.parse(data?.value || '{}') } catch { return null }
}

/** Actualizar datos del propio perfil. */
export async function actualizarMiPerfil(campos) {
  const updates = {}
  if (campos.nombre !== undefined) updates.nombre = campos.nombre?.trim() || null
  if (campos.phone !== undefined) updates.phone = campos.phone?.trim() || null
  // No dejamos editar email/documento sin verificación → solo admin puede
  const { error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', (await supabase.auth.getUser()).data.user.id)
  if (error) throw error
}

/** Cambiar contraseña. */
export async function cambiarMiPassword(nuevaPassword) {
  const { error } = await supabase.auth.updateUser({ password: nuevaPassword })
  if (error) throw error
}

/** Direcciones únicas usadas por el cliente en pedidos one-shot pasados. */
export async function getDireccionesHistoricas() {
  const { data, error } = await supabase.rpc('cliente_direcciones_historicas')
  if (error) { console.error('getDireccionesHistoricas:', error); return [] }
  return data || []
}

/** Lista de barrios activos (para el wizard). */
export async function getBarriosActivos() {
  const { data, error } = await supabase
    .from('barrios')
    .select('id, nombre, dias, zona_codigo')
    .eq('activo', true)
    .order('nombre')
  if (error) return []
  return data || []
}

/* ──────────── PEDIDOS (one-shot: kit unico, tienda) ──────────── */

/** Lista los productos vendibles para La Tienda (ordenados por categoria/tier/precio). */
export async function getProductosTienda() {
  const { data, error } = await supabase.rpc('tienda_get_productos')
  if (error) { console.error('getProductosTienda:', error); return [] }
  return data || []
}

/** Pedidos del cliente actual (one-shot, no suscripciones). */
export async function getMisPedidos(limit = 20) {
  const { data, error } = await supabase.rpc('cliente_get_mis_pedidos', { p_limit: limit })
  if (error) { console.error('getMisPedidos:', error); return [] }
  return data || []
}

/**
 * Crea un pedido one-shot (kit unico, productos sueltos o adicional de suscriptor).
 * El backend valida envio, calcula fecha_corte y crea el pedido.
 *
 * @param {Object} p
 * @param {'compra_unica'|'productos_sueltos'|'adicional_suscripcion'} p.tipo
 * @param {string|null} [p.kitCodigo]
 * @param {Array<{sku, cantidad, precio_unit, nombre, presentacion}>} [p.items]
 * @param {string} p.barrioId
 * @param {string} p.direccion
 * @param {string} [p.apto]
 * @param {number} [p.lat]
 * @param {number} [p.lng]
 * @param {string} p.diaSemana          'lunes' | 'martes' | etc
 * @param {'manana'|'tarde'} [p.ventana='manana']
 * @param {string} p.fechaEntrega       'YYYY-MM-DD'
 * @param {boolean} [p.fueraDeCorte=false]
 * @param {'transferencia'|'tarjeta_credito'|'tarjeta_debito'|'abitab'|'redpagos'|'mercadopago'} [p.metodoPago]
 * @param {string} [p.destinatarioNombre]   ej: "Para mi mamá"
 * @param {string} [p.destinatarioTelefono]
 * @param {string} [p.compradorNombre]
 * @param {string} [p.compradorTelefono]
 * @param {string} [p.compradorEmail]
 * @param {string} [p.notas]
 *
 * @returns {Promise<Object>} pedido completo con id, numero_orden, fecha_corte
 */
export async function crearPedido(p) {
  const { data, error } = await supabase.rpc('cliente_crear_pedido', {
    p_tipo:                  p.tipo,
    p_kit_codigo:            p.kitCodigo || null,
    p_items:                 p.items || [],
    p_barrio_id:             p.barrioId,
    p_direccion:             p.direccion,
    p_apto:                  p.apto || null,
    p_lat:                   p.lat || null,
    p_lng:                   p.lng || null,
    p_dia_semana:            p.diaSemana,
    p_ventana:               p.ventana || 'manana',
    p_fecha_entrega:         p.fechaEntrega,
    p_destinatario_nombre:   p.destinatarioNombre || null,
    p_destinatario_telefono: p.destinatarioTelefono || null,
    p_comprador_nombre:      p.compradorNombre || null,
    p_comprador_telefono:    p.compradorTelefono || null,
    p_comprador_email:       p.compradorEmail || null,
    p_metodo_pago:           p.metodoPago || null,
    p_fuera_de_corte:        !!p.fueraDeCorte,
    p_notas:                 p.notas || null,
    p_cliente_suscriptor_id: p.clienteSuscriptorId || null,
    p_perfil_facturacion_id: p.perfilFacturacionId || null,
  })
  if (error) throw error
  return data
}

/**
 * Crea preapproval MP para la suscripción del cliente.
 * - Con `cardTokenId` (SDK MP JS tokenizó tarjeta en frontend, task #4): preapproval queda status=authorized directo,
 *   sin redirect a checkout hosted. Evita el bug ESC.
 * - Sin `cardTokenId`: fallback status=pending + init_point (redirect a checkout MP).
 *
 * Flow original 8-jul-2026 (task #36): mp-crear-preapproval reemplazó a mp-guardar-tarjeta.
 * Actualización task #4 (8-jul-2026): agregado card_token_id opcional para tokenización frontend.
 */
export async function crearPreapprovalCliente({ clienteId, email, monto, startDate, cardTokenId, payerId, firstName, lastName, identification, sessionId, deviceSessionId }) {
  const { data, error } = await supabase.functions.invoke('mp-crear-preapproval', {
    body: {
      cliente_id: clienteId,
      email,
      monto,
      start_date: startDate,  // ISO date string, ej: '2026-07-09'
      ...(cardTokenId ? { card_token_id: cardTokenId, payer_id: payerId } : {}),
      // Payer completo (P0 antifraude MP): sólo mandamos los campos que tenemos.
      ...(firstName ? { first_name: firstName } : {}),
      ...(lastName ? { last_name: lastName } : {}),
      ...(identification?.type && identification?.number ? { identification } : {}),
      // session_id (FIX 2 · 18-jul-2026): encadena logs del front (log-front-error) con
      // los del edge (signup_errors_log v21+). Mismo UUID por sesión del wizard.
      ...(sessionId ? { session_id: sessionId } : {}),
      // Device ID de MP (P0 antifraude 2026-07-22): window.MP_DEVICE_SESSION_ID capturado por
      // el Brick. El edge lo manda como header X-meli-session-id en el POST /preapproval.
      ...(deviceSessionId ? { device_session_id: deviceSessionId } : {}),
    },
  })
  // UX 18-jul-2026 (post caso Marcela): en vez de tirar "Edge Function returned a
  // non-2xx status code" (mensaje default de supabase.functions.invoke() ante 4xx/5xx),
  // extraemos el error_code real de MP del body de la respuesta y lo traducimos a un
  // mensaje humano en español rioplatense usando mpErrorHumano.js.
  if (error) {
    let bodyData = null
    try {
      // supabase-js v2 guarda el Response original en error.context.response.
      // Lo clonamos porque puede ser un stream que ya fue consumido.
      const resp = error?.context?.response
      if (resp && typeof resp.clone === 'function') {
        bodyData = await resp.clone().json().catch(() => null)
      }
    } catch { /* si falla el parseo, mostramos mensaje genérico */ }
    const mpCode = extraerCodigoMP(bodyData)
    const humano = mpErrorHumano(mpCode)
    const err = new Error(humano)
    err.mpCode = mpCode || null
    err.mpDetail = bodyData?.detalle || null
    err.mpStatus = bodyData?.mp_status || null
    throw err
  }
  if (data?.error) {
    const mpCode = extraerCodigoMP(data)
    const err = new Error(mpErrorHumano(mpCode, data.error))
    err.mpCode = mpCode || null
    err.mpDetail = data?.detalle || null
    throw err
  }
  return data  // { ok, preapproval_id, status, init_point, next_payment_date, ambiente, autorizado_directo }
}

// ============================================================================
// Suscripción 3DS (flag susc_flujo_mes1 = 'payment_3ds') — flujo nuevo, mes1 on-session con 3DS.
// Convive con crearPreapprovalCliente (legacy) detrás del flag. Ver DISENO-SUSCRIPCION-3DS.md.
// ============================================================================

// Helper interno: invoca un edge MP y traduce el error de MP a mensaje humano (como
// crearPreapprovalCliente). Devuelve el data crudo (el caller inspecciona ok/estado/challenge).
async function invocarEdgeMP(nombreEdge, body) {
  const { data, error } = await supabase.functions.invoke(nombreEdge, { body })
  if (error) {
    let bodyData = null
    try {
      const resp = error?.context?.response
      if (resp && typeof resp.clone === 'function') bodyData = await resp.clone().json().catch(() => null)
    } catch { /* genérico */ }
    const mpCode = extraerCodigoMP(bodyData)
    const err = new Error(mpErrorHumano(mpCode))
    err.mpCode = mpCode || null
    err.mpDetail = bodyData?.detalle || bodyData?.status_detail || null
    err.mpStatus = bodyData?.mp_status || null
    throw err
  }
  // Nota: estos edges devuelven 200 con { ok:false, ... } para rechazos "de negocio"
  // (rechazado/challenge/preapproval_fallo). El caller decide; NO tiramos error acá salvo
  // que venga un data.error de infraestructura.
  if (data?.error && !data?.status && !data?.requiere_challenge) {
    const mpCode = extraerCodigoMP(data)
    const err = new Error(mpErrorHumano(mpCode, data.error))
    err.mpCode = mpCode || null
    err.mpDetail = data?.status_detail || data?.detalle || null
    throw err
  }
  return data
}

/**
 * Paso 1 del flujo 3DS: pago mes1 on-session (Payments API + three_d_secure_mode).
 * Devuelve { ok, checkout_id, status, status_detail, requiere_challenge, payment_id, three_ds_info, ... }
 */
export async function iniciarPagoMes1Susc({ clienteId, email, monto, montoMes2, cardTokenId, firstName, lastName, identification, sessionId, deviceSessionId }) {
  return invocarEdgeMP('mp-suscripcion-mes1', {
    cliente_id: clienteId,
    email,
    monto,
    ...(montoMes2 != null ? { monto_mes2: montoMes2 } : {}),
    card_token_id: cardTokenId,
    ...(firstName ? { first_name: firstName } : {}),
    ...(lastName ? { last_name: lastName } : {}),
    ...(identification?.type && identification?.number ? { identification } : {}),
    ...(sessionId ? { session_id: sessionId } : {}),
    ...(deviceSessionId ? { device_session_id: deviceSessionId } : {}),
  })
}

/**
 * Paso 2 del flujo 3DS (leg D): confirma el pago mes1 y, si está approved, crea el preapproval
 * mes2+ con el token B. Se usa tanto para el polling del challenge como para disparar el leg D.
 * Devuelve { ok, pago_aprobado, requiere_challenge, rechazado, activada, preapproval_fallo, ... }
 */
export async function activarSuscMes1Status({ clienteId, cardTokenIdB, email, firstName, lastName, identification, sessionId, deviceSessionId }) {
  return invocarEdgeMP('mp-suscripcion-mes1-status', {
    cliente_id: clienteId,
    ...(cardTokenIdB ? { card_token_id_b: cardTokenIdB } : {}),
    ...(email ? { email } : {}),
    ...(firstName ? { first_name: firstName } : {}),
    ...(lastName ? { last_name: lastName } : {}),
    ...(identification?.type && identification?.number ? { identification } : {}),
    ...(sessionId ? { session_id: sessionId } : {}),
    ...(deviceSessionId ? { device_session_id: deviceSessionId } : {}),
  })
}

/** Crea una suscripción nueva como cliente self-service. */
export async function crearMiSuscripcion(params) {
  const { data, error } = await supabase.rpc('cliente_crear_mi_suscripcion', {
    alias_param:        params.alias || null,
    barrio_id_param:    params.barrioId,
    direccion_param:    params.direccion,
    apto_param:         params.apto || null,
    tipo_vivienda_param: params.tipoVivienda || null,
    kit_codigo_param:   params.kitCodigo || null,
    dia_entrega_param:  params.diaEntrega || null,
    ventana_param:      params.ventana || 'manana',
    semana_mes_param:   params.semanaMes || null,
    dia_cobro_mes_param: params.diaCobroMes || null,
    notas_param:        params.notas || null,
    lat_param:          params.lat || null,
    lng_param:          params.lng || null,
    primera_entrega_fecha_param:   params.primeraEntregaFecha || null,
    primera_entrega_recargo_param: Number(params.primeraEntregaRecargo || 0),
    primera_entrega_tipo_param:    params.primeraEntregaTipo || null,
    perfil_facturacion_id_param:   params.perfilFacturacionId || null,
  })
  if (error) throw error
  return data
}

/**
 * Devuelve las opciones para la PRIMERA entrega de una suscripcion.
 * Si esta dentro de corte: 1 opcion 'esperar' (gratis, dia natural).
 * Si esta fuera de corte: hasta 5 opciones (1 esperar + 1 saltar_corte + 3 fuera_ruta).
 *
 * @param {Object} p
 * @param {string} p.barrioId
 * @param {string} p.diaEntrega    'lunes' | 'martes' | ...
 * @param {number} p.semanaMes     1..4
 * @param {Date}   [p.desde]       default: ahora
 *
 * @returns {Promise<Array<{
 *   opcion: 'esperar'|'saltar_corte'|'fuera_ruta',
 *   fechaEntrega: string,         // 'YYYY-MM-DD'
 *   nombreDia: string,            // 'lunes' | 'martes' | ...
 *   fechaCorte: string,           // ISO timestamp
 *   recargo: number,              // 0 o tarifa fuera_ruta
 *   enRuta: boolean,
 *   esDiaElegido: boolean,
 * }>>}
 */
/* ──────────── DIRECCION DE PERFIL (sin compra) ──────────── */

/**
 * Estado actual de vecinos en la cuadra del usuario.
 * Prioridad de dirección: perfil → suscripción → último pedido.
 * Funciona para cualquier usuario logueado, incluso sin compras.
 */
export async function getVecinosEnMiCuadra() {
  const { data, error } = await supabase.rpc('cliente_vecinos_en_mi_cuadra')
  if (error) { console.error('getVecinosEnMiCuadra:', error); return null }
  return Array.isArray(data) ? (data[0] || null) : (data || null)
}

/* ──────────── BAJA DE CUENTA ──────────── */

/** Chequea si el cliente puede dar de baja su cuenta. Devuelve motivos bloqueantes si no. */
export async function puedoDarDeBaja() {
  const { data, error } = await supabase.rpc('cliente_puedo_dar_de_baja')
  if (error) { console.error('puedoDarDeBaja:', error); return null }
  return data
}

/** Da de baja la cuenta (soft delete). Razón opcional. */
export async function darDeBajaMiCuenta(motivo) {
  const { data, error } = await supabase.rpc('cliente_dar_de_baja_mi_cuenta', {
    p_motivo: motivo || null,
  })
  if (error) throw error
  return data
}

/** Trae la dirección de referencia que el usuario guardó en su perfil. */
export async function getDireccionPerfil() {
  const { data, error } = await supabase.rpc('cliente_perfil_get_direccion')
  if (error) { console.error('getDireccionPerfil:', error); return null }
  return Array.isArray(data) ? (data[0] || null) : (data || null)
}

/** Facturas del cliente actual (CFE/NC emitidas por Limpack).
 *  IMPORTANTE: filtra explícitamente por pedidos del usuario logueado.
 *  Sin este filtro, un superadmin/admin vería TODAS las facturas (las policies admin son OR con cliente).
 *  Devuelve hasta `limit` ordenadas por fecha de emisión desc.
 *  Cada fila trae el shortlink limpack.uy/f/{hash} para abrir el PDF.
 */
export async function getMisFacturas(userId, limit = 20) {
  if (!userId) return []

  // 1. IDs de pedidos one-shot del usuario
  const { data: peds } = await supabase
    .from('pedidos')
    .select('id')
    .eq('comprador_profile_id', userId)
  const pedidoIds = (peds || []).map(p => p.id)

  // 2. IDs de suscripciones donde es titular o beneficiario
  const { data: suscs } = await supabase
    .from('clientes')
    .select('id')
    .or(`titular_id.eq.${userId},beneficiario_id.eq.${userId}`)
  const clienteIds = (suscs || []).map(s => s.id)

  if (pedidoIds.length === 0 && clienteIds.length === 0) return []

  // 3. Facturas que matchean por alguno de los dos
  const conditions = []
  if (pedidoIds.length > 0) conditions.push(`pedido_id.in.(${pedidoIds.join(',')})`)
  if (clienteIds.length > 0) conditions.push(`cliente_id.in.(${clienteIds.join(',')})`)

  const { data, error } = await supabase
    .from('facturas_emitidas')
    .select('id, tipo_cfe, serie, numero, fecha_emision, monto_total, moneda, estado, estado_dgi, shortlink_hash, pedido_id, cliente_id, created_at')
    .or(conditions.join(','))
    .order('fecha_emision', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) { console.error('getMisFacturas:', error); return [] }
  return data || []
}

/** Guarda/actualiza la dirección de perfil. */
export async function guardarDireccionPerfil({ direccion, apto, barrio_id, referencias }) {
  const { data, error } = await supabase.rpc('cliente_perfil_guardar_direccion', {
    p_direccion: direccion,
    p_apto: apto || null,
    p_barrio_id: barrio_id,
    p_referencias: referencias || null,
  })
  if (error) throw error
  return data
}

export async function simularOpcionesPrimeraEntrega({ barrioId, diaEntrega, semanaMes, desde }) {
  if (!barrioId || !diaEntrega || !semanaMes) return []
  const { data, error } = await supabase.rpc('simular_opciones_primera_entrega', {
    p_barrio_id:   barrioId,
    p_dia_entrega: diaEntrega,
    p_semana_mes:  Number(semanaMes),
    p_desde:       (desde || new Date()).toISOString(),
  })
  if (error) throw error
  return (data || []).map(r => ({
    opcion:       r.opcion,
    fechaEntrega: r.fecha_entrega,
    nombreDia:    r.nombre_dia,
    fechaCorte:   r.fecha_corte,
    recargo:      Number(r.recargo || 0),
    enRuta:       r.en_ruta,
    esDiaElegido: r.es_dia_elegido,
  }))
}

// OneShot (tienda de sueltos, LimpackFlex) — logueado. Crea el pedido via crear_pedido_oneshot.
// pedido = { oneshot:true, tipo:'productos_sueltos', items, entrega_barrio_geo, entrega_localidad,
//            comprador_documento, comprador_nombre/telefono/email, entrega_direccion, ... }
export async function crearPedidoOneshot(userId, pedido) {
  const { data, error } = await supabase.rpc('crear_pedido_oneshot', {
    p_user_id: userId,
    p_pedido: pedido,
  })
  if (error) throw error
  return data
}
